import { useMemo } from 'react';

import { useMytaverseEvent } from '@core/dashboard/providers';
import type { IMapLayoutCatalog } from '@interfaces/map';

import {
  CatalogSource,
  MapEditorCatalogElement,
  resolveCatalogElements,
} from './mapEditorCatalog';

export interface UseMapEditorCatalogResult {
  elements: MapEditorCatalogElement[];
  // 'build' means the streamed build shipped its own catalog through the GameCast metadata
  // provider; 'fallback' means it did not, and `elements` comes back empty. useCanUseMapEditor
  // reads this to decide whether the map has a map editor at all.
  source: CatalogSource;
  // Id of the element that closes a chain, already checked against `elements`. Empty when this
  // build ships no cap — the chain then ends in an open doorway, which is a valid layout.
  defaultEndElement: string;
  // Id of the lobby this kit considers its default, already checked against `elements`. Empty when
  // the build names none, which is every build older than the feature.
  //
  // The value the lobby control pre-selects, and nothing more: it is never published on the
  // author's behalf. See the layout's own `startElement` for the choice that does reach the wire.
  defaultStartElement: string;
  // The catalog these elements were read out of, for the parts of the kit this hook does not
  // normalise — the finish registry. Carried rather than looked up a second time elsewhere, so the
  // registry and the elements can never come from two different reads of the provider.
  catalog?: IMapLayoutCatalog;
}

/**
 * Rewrites an authored catalog URL to something this hub can actually request.
 *
 * @param authored - The catalog's own value, relative in every case the build ships today.
 * @param published - The URL the upload published for this element and kind, or nothing.
 * @returns The published URL, an already-absolute authored one, or an empty string.
 */
const resolveImageUrl = (authored: string, published?: string): string =>
  published || (/^https?:\/\//i.test(authored) ? authored : '');

/**
 * Rewrites each element's `thumbnailUrl` and `previewUrl` to something this hub can request.
 *
 * The catalog ships a path relative to wherever the build's `WebEditor/` is hosted, which is not a
 * URL from here: put into an `<img src>` it resolves against the current SPA route and the SPA
 * fallback answers HTTP 200 with a page of HTML — a request that succeeds and then fails to decode.
 * That is the whole of MV-1953, and it looked from the hub like a build shipping no renders.
 *
 * So the relative form is **replaced**, never joined with here: the upload publishes each render
 * out of the build package and records the absolute URL per element id, and an element with no
 * published render is left with nothing, which is what sends every consumer to the drawing without
 * emitting a doomed request. An already-absolute value is kept — nothing writes one today and the
 * catalog contract rules it out, but it is the one form of the field usable on its own.
 *
 * Both kinds are one capture — the tile is the full-size shot downscaled — but they are published
 * as two maps and resolved independently, because a build routinely ships one and not the other:
 * the render is opt-in and off by default, so tiles committed beside the catalog are common while
 * previews are usually absent entirely.
 *
 * @param elements - Elements as the catalog declared them.
 * @param publishedThumbnails - `clientParams.layoutThumbnails`, or nothing.
 * @param publishedPreviews - `clientParams.layoutPreviews`, or nothing.
 * @returns The same elements, with both URLs resolved or emptied — and the very same object
 *          wherever neither changed, which is most of a kit and must not cost a re-render.
 */
const withResolvedImages = (
  elements: MapEditorCatalogElement[],
  publishedThumbnails?: Record<string, string>,
  publishedPreviews?: Record<string, string>,
): MapEditorCatalogElement[] =>
  elements.map((element) => {
    const thumbnailUrl = resolveImageUrl(
      element.thumbnailUrl ?? '',
      publishedThumbnails?.[element.id],
    );
    const previewUrl = resolveImageUrl(
      element.previewUrl ?? '',
      publishedPreviews?.[element.id],
    );

    return thumbnailUrl === element.thumbnailUrl &&
      previewUrl === element.previewUrl
      ? element
      : { ...element, thumbnailUrl, previewUrl };
  });

/**
 * The room kit shipped by the build being streamed.
 *
 * Read from that build's own record and from nowhere else — `clientParams.layoutCatalogs`, keyed by
 * the ueIdentifier of the map each catalog names as its host. A kit describes the geometry of one
 * build, so the only copy that can be trusted is the one belonging to the build on the wire.
 *
 * The map-level slot `map.layoutCatalog` is deliberately not read any more (MV-2004). It was one
 * entry per map, shared by every build that named the map and written last-writer-wins, so it served
 * one build's geometry to another: it is frozen at whichever upload wrote it last, kits have grown
 * since, and which of the two an author saw depended on whether the build they happened to be
 * streaming shipped a kit of its own. Nothing writes it now either; the upload writes only the
 * build's record. The measurements behind this are in apps/admin/docs/branding/hub.md.
 *
 * So a build that ships no kit reports `source: 'fallback'` with no elements, and useCanUseMapEditor
 * hides every map-editor surface for it. That is the answer rather than a gap to fill: such a build
 * has no rooms to offer and no geometry to spawn, and the only list this hub could put in front of
 * the author instead is some other build's.
 *
 * The published renders follow the same whole-or-nothing rule: they are keyed by element
 * id and published out of **this build's** package, so the only elements they can be shown
 * against are the ones of the build that shipped them. A build with no kit has no renders
 * either, which is also the truth — nothing ever published any for a pre-move build — and an
 * element whose render was not published is left with nothing rather than the relative path,
 * since nothing can serve that one either way.
 *
 * @returns The normalised elements with their renders resolved, where the catalog came from,
 * the id of the chain cap, the id of the kit's default lobby, and the catalog itself for
 * readers of the kit's other sections.
 */
export const useMapEditorCatalog = (): UseMapEditorCatalogResult => {
  const { currentRoom, pixelStreamingClient } = useMytaverseEvent();

  const hostUeIdentifier = currentRoom?.map?.ueIdentifier;
  const shippedCatalogs = pixelStreamingClient?.clientParams?.layoutCatalogs;
  const shippedThumbnails =
    pixelStreamingClient?.clientParams?.layoutThumbnails;
  const shippedPreviews = pixelStreamingClient?.clientParams?.layoutPreviews;

  return useMemo(() => {
    const shipped = hostUeIdentifier
      ? shippedCatalogs?.[hostUeIdentifier]
      : undefined;

    // `resolveCatalogElements` reads an absent catalog and one with no elements the same way, which
    // is what makes it the test for "this build has a kit" rather than checking the field itself.
    const fromBuild = resolveCatalogElements(shipped);

    return {
      ...fromBuild,
      elements: withResolvedImages(
        fromBuild.elements,
        shippedThumbnails,
        shippedPreviews,
      ),
      catalog: shipped,
    };
  }, [shippedCatalogs, shippedThumbnails, shippedPreviews, hostUeIdentifier]);
};
